// GIFs from KLIPY, straight from the browser like the app (app/.../gif/Klipy.kt): KLIPY's terms
// allow no proxy and no copies, so the client searches and every client loads the pictures from
// KLIPY's URLs. The message only carries them (docs: GIF kind, app/.../gif/Gif.kt).

import type { Gif } from "../data/content.ts";

declare const __KLIPY_APP_KEY__: string;

const BASE = "https://api.klipy.com";
export const PER_PAGE = 24;

export function available(): boolean {
  return typeof __KLIPY_APP_KEY__ === "string" && __KLIPY_APP_KEY__.length > 0;
}

/** A random id per browser, as KLIPY asks; not the Voxie account. */
function customerId(): string {
  try {
    let id = localStorage.getItem("voxie.gif.customer");
    if (!id) localStorage.setItem("voxie.gif.customer", (id = crypto.randomUUID()));
    return id;
  } catch {
    return (sessionCustomer ??= crypto.randomUUID());
  }
}
let sessionCustomer: string | undefined;

function locale(): string {
  const region = navigator.language.split("-")[1];
  return (region ?? "at").toLowerCase();
}

interface KlipyFile {
  url?: string;
  width?: number;
  height?: number;
}
interface KlipyItem {
  type?: string;
  slug?: string;
  title?: string;
  file?: Record<"hd" | "md" | "sm", { webp?: KlipyFile; jpg?: KlipyFile } | undefined>;
}

export interface GifPage {
  gifs: Gif[];
  page: number;
  hasNext: boolean;
}

/** Trending without [query], otherwise a search; KLIPY's order is kept. */
export async function gifs(query: string, page = 1, signal?: AbortSignal): Promise<GifPage> {
  const q = query.trim();
  const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE), customer_id: customerId(), locale: locale(), format_filter: "webp,jpg" });
  if (q) params.set("q", q);
  const response = await fetch(`${BASE}/api/v1/${__KLIPY_APP_KEY__}/gifs/${q ? "search" : "trending"}?${params}`, { signal });
  if (!response.ok) throw new Error(`KLIPY ${response.status}`);
  const json = (await response.json()) as { data?: { data?: KlipyItem[]; current_page?: number; has_next?: boolean } };
  return { gifs: (json.data?.data ?? []).map(toGif).filter((g): g is Gif => g !== null), page: json.data?.current_page ?? page, hasNext: json.data?.has_next ?? false };
}

/** Tells KLIPY a GIF was sent (their ranking); errors do not matter. */
export function shared(gif: Gif, query: string) {
  const body: Record<string, string> = { customer_id: customerId() };
  if (query.trim()) body.q = query.trim();
  void fetch(`${BASE}/api/v1/${__KLIPY_APP_KEY__}/gifs/share/${encodeURIComponent(gif.slug)}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }).catch(() => undefined);
}

const https = (f: KlipyFile | undefined) => (f?.url?.startsWith("https://") ? f : undefined);

export function toGif(item: KlipyItem): Gif | null {
  if (item.type !== "gif" || !item.slug) return null;
  const f = item.file;
  const main = https(f?.md?.webp) ?? https(f?.hd?.webp) ?? https(f?.sm?.webp);
  if (!main?.url || !main.width || !main.height) return null;
  const preview = https(f?.sm?.webp)?.url ?? main.url;
  const still = https(f?.md?.jpg)?.url ?? https(f?.sm?.jpg)?.url;
  return { provider: "klipy", slug: item.slug, title: item.title ?? "", url: main.url, preview, still, w: main.width, h: main.height };
}
